import React, { useEffect } from 'react';
import { Alert, Box, Button, Skeleton, Typography } from 'housecall-ui';
import Chip from 'housecall-ui/dist/DesignSystem/DataDisplay/Chip/Chip';
import Lock from 'housecall-ui/dist/IconsAndImages/Icons/Lock';

import { InvoiceData } from '../../api/payment/api';
import { Layout } from '../../components/Layout';
import { ScrollableContent, scrollToTop } from '../../components/ScrollableContent';
import { Spinner } from '../../components/Spinner';
import { StepProgressBar } from '../../components/StepProgressBar';
import { useBookingConfirmPaymentData } from '../../hooks/useBookingConfirmationMutation';
import { trackButtonClicked, useAnalyticsHomeownerParams } from '../../hooks/useEventTracking';
import { useInvoice } from '../../hooks/useInvoice';
import { formatMoney } from '../../utils/formatMoney';

import { usePaymentStatus } from './hooks/usePaymentStatus';
import CreditCardForm from './CreditCardForm';
import InvoiceDetails from './InvoiceDetails';
import { PaymentSkeleton } from './PaymentSkeleton';

export const getButtonLabel = (invoiceData: InvoiceData) => {
  const hasDeposit = invoiceData?.template === 'deposit';
  const amountText = formatMoney(invoiceData?.dueAmount);

  return hasDeposit ? `Pay deposit ${amountText}` : `Pay ${amountText}`;
};

export const PaymentStep = () => {
  const paymentData = useBookingConfirmPaymentData();
  const paymentStatus = usePaymentStatus(paymentData?.invoiceToken);
  const trackingProps = useAnalyticsHomeownerParams();

  const { data: invoiceData, isLoading, isError } = useInvoice(paymentData?.invoiceToken);

  const hasDeposit = invoiceData?.template === 'deposit';

  const onClick = () => {
    trackButtonClicked({
      buttonName: 'ob_pay',
      ...trackingProps,
    });
    document.querySelector('#payment-form')?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
  };

  useEffect(() => {
    if (paymentStatus?.isError) {
      scrollToTop();
    }
  }, [paymentStatus?.isError]);

  return (
    <Layout
      Header={<StepProgressBar currentStep={5} stepCount={5} />}
      ContentComponent={
        <ScrollableContent>
          <Spinner show={paymentStatus?.inProgress || false} />
          {hasDeposit && (
            <Box mb={2}>
              <Alert severity="info">Deposit payment only - the remaining balance will be charged later.</Alert>
            </Box>
          )}
          {paymentStatus?.isError && (
            <Box mb={2}>
              <Alert severity="error">We could not proceed your payment, please try again.</Alert>
            </Box>
          )}
          <Typography variant="h5" gutterBottom>
            Payment method
          </Typography>
          {isLoading ? (
            <PaymentSkeleton />
          ) : (
            <div>
              {isError || !invoiceData ? (
                <Box my={2}>
                  <Alert severity="error">Error occured while loading the invoice data</Alert>
                </Box>
              ) : (
                <>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body1">Enter card details below</Typography>
                    <Chip icon={<Lock size="small" color="primary" />} size="small" label="Secure" color="info" />
                  </Box>
                  <Box mt={2}>
                    <CreditCardForm />
                  </Box>

                  <Box my={2}>
                    <InvoiceDetails invoiceData={invoiceData} />
                  </Box>
                </>
              )}
            </div>
          )}
        </ScrollableContent>
      }
      Buttons={
        invoiceData ? (
          <Button version="v2" size="medium" fullWidth variant="contained" color="secondary" onClick={onClick}>
            {getButtonLabel(invoiceData)}
          </Button>
        ) : (
          <Skeleton variant="rect" height={48} width="100%" />
        )
      }
    />
  );
};
